import { useCallback, useEffect, useState } from 'react'

import { Card, Icon, SkeletonBox } from '@circlefin/components'
import { useModal } from '@circlefin/modal-router'
import { routes } from '@services/sections/modal/routes'
import {
  SubscriptionsDocument,
  UpdateAutoPayDocument,
  useSubscriptionsQuery,
  useUpdateAutoPayMutation,
} from '@shared/graphql'
import { useMfa } from '@shared/mfa'
import useTranslation from 'next-translate/useTranslation'

import * as Buttons from '../../buttons'

/**
 * Auto Pay Enroll component to show status of an entity's autopay settings.
 */
export const AutoPayEnroll: React.FC = () => {
  const { t } = useTranslation('settings')
  const { router, events } = useModal()
  const [optimisticToggle, setOptimisticToggle] = useState<boolean | undefined>(
    undefined,
  )
  const [withMfa, { context, mfaError, mfaSuccess }] = useMfa()
  const modal = useModal()
  const { data, loading } = useSubscriptionsQuery()
  const autoPayEnabled = data?.subscriptions.automaticPaymentEnabled

  const [setAutoPay] = useUpdateAutoPayMutation({
    refetchQueries: [UpdateAutoPayDocument, SubscriptionsDocument],
    awaitRefetchQueries: true,
    onCompleted: () => {
      mfaSuccess({
        message: t`common:success`,
        description: t`billing.autopay.disable`,
      })
    },
    onError: (error) => {
      mfaError({ error })
    },
  })

  /**
   * Add modal event listeners.
   */
  useEffect(() => {
    const onModalClose = () => {
      // Turn off optimistic toggle on modal close
      setOptimisticToggle(undefined)
    }

    events.on('onCloseStart', onModalClose)

    return () => {
      events.off('onCloseStart', onModalClose)
    }
  }, [events])

  const handleDisableAutopay = useCallback(() => {
    withMfa({
      onComplete: (code) => {
        void setAutoPay({
          variables: {
            automaticPaymentEnabled: false,
          },
          context: context(code),
        })
      },
      onAbort: () => modal.close({ context: 'onDismiss' }),
    })
  }, [context, modal, setAutoPay, withMfa])

  const handleAutoPayToggle = useCallback(() => {
    // Turn on optimistic toggle on modal open
    setOptimisticToggle(!autoPayEnabled)

    // Display confirmation modal on turn-on
    if (!autoPayEnabled) {
      router.push({
        pathname: routes.account.billing.autoPay.enable,
      })
    } else {
      // Display MFA on turn-off
      handleDisableAutopay()
    }
  }, [autoPayEnabled, handleDisableAutopay, router])

  return (
    <SkeletonBox className="h-40 rounded-lg" loading={loading}>
      <Card data-testid="autopay-card">
        <Card.Content className="px-10 pb-10 pt-0">
          <div className="flex space-y-9 pt-9 font-circular">
            <div className="flex space-x-10">
              <div className="flex grow flex-col justify-center">
                <div className="flex items-center justify-between">
                  <SkeletonBox className="h-8 w-1/3" loading={loading}>
                    <div className="flex items-center justify-between space-x-2">
                      <Icon name="RefreshOutline" size={24} />
                      <h3
                        className="text-2xl leading-8 font-circular-book"
                        data-testid="header"
                      >
                        {autoPayEnabled
                          ? t('billing.autopay-enroll.headers.AutoPayEnabled')
                          : t('billing.autopay-enroll.headers.AutoPayDisabled')}
                      </h3>
                    </div>
                  </SkeletonBox>
                  <Buttons.Toggles.BillingDashboard
                    data-testid="toggle"
                    onChange={handleAutoPayToggle}
                    selected={optimisticToggle ?? autoPayEnabled}
                  />
                </div>
                <p
                  className="mt-2 max-w-4/5 text-base leading-7 text-black-600 font-circular-regular"
                  data-testid="description"
                >
                  {autoPayEnabled
                    ? t(`billing.autopay-enroll.descriptions.AutoPayEnabled`)
                    : t(`billing.autopay-enroll.descriptions.AutoPayDisabled`)}
                </p>
              </div>
            </div>
          </div>
        </Card.Content>
      </Card>
    </SkeletonBox>
  )
}
